import React, { useMemo, Fragment, useCallback, useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { withTheme } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import { FovPopover } from 'components/generic';
import { DocNote, FovModal, SearchInputField } from 'components';
import { MESSAGE_TYPES } from 'components/notifications';
import {
  VerticalSpacer,
  HorizontalSpacer,
  ThemeSwitcher,
  FovDivider,
} from 'components/layout';
import { UserOutlined, CreditsOutlined } from 'components/icons';
import { Logo, ArchvisionLogo } from '..';
import { createRpc, getModelMetadata, getRpcCategories, clearModelMetadata, setPublishName } from 'store/actions/rpc';
import { getUserData, invalidateAuth, setUserPreferences } from 'store/actions/userData';
import { addMessage, clearMessages } from 'store/actions/app';
import { AppHeaderFileName } from '../blocks';
import { buildAuthUrl, hasAccess } from 'navigation/navigationHelpers';
import { decode, clearImpersonationBackup } from 'utils';

import {
  AppHeaderContainer,
  LogoContainer,
  HeaderLogoGroup,
  MenuLink,
  BasicLink,
  MenuItem,
  ProfileMenuContainer,
  Profile,
  Credits,
  CreditsContainer,
  AddCreditsButton,
  InfoAndSupport,
  NormalMenuItem,
  HelpIcon,
  VerticalDivider,
  ExploreButton,
  HeaderButton,
  Search,
  MenuContainer,
  FileNameContainer,
  Instructions,
  BetaBadge,
  MenuItemContainer,
} from './appHeaderStyles';
import { getAvailChannels, availLogout } from 'store/actions/avail';
import { wssCallback } from 'utils/jobSockets';
import { ProtectedContent, allRoles, superUsers } from 'components/generic/ProtectedContent';
import {
  setSerializedData,
  clearUpdateData,
  setReinitializeCanvas,
  setExportModel,
  setExportedModelData,
  setGuidURL,
} from 'store/actions/canvas';
import RPCForm from 'pages/ModelCanvas/SideMenus/RPCForm';
import AvailLoginContainer from 'pages/AvailLoginContainer';
import constants from 'constants/index';
import docsItems from 'pages/Documentation/docItems';
import { paramsFromURL, paramsToObject } from 'utils/paramHelpers';


const { ARCHVISION_TOKEN } = constants.TOKEN_NAMES;

const AppHeader = withTheme(props => {
  const { theme } = props;

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const userDataStore = useSelector(state => state.userData);
  const rpcStore = useSelector(state => state.rpc);
  const [ availModalOpen, setAvailModalOpen ] = useState(false);
  const [ modalOpen, setModalOpen ] = useState(false);
  const [ rpcCreateType, setRpcCreateType ] = useState();
  const [ createPopover, setCreatePopover ] = useState(false);
  const [ helpPopover, setHelpPopover ] = useState(false);
  const [ profilePopover, setProfilePopover ] = useState(false);
  const [ creditsPopover, setCreditsPopover ] = useState(false);
  const [ , , removeCookie ] = useCookies([ 'userAuth' ]);
  const modelsPage = pathname.includes('/models');
  const teamsPage = pathname.includes('/team-models/');
  const onMyFiles = pathname.includes('/my-files');
  const onJobs = pathname.includes('/jobs');
  const onWelcome = pathname.includes('/welcome');
  const showSearch = onWelcome;
  const guid = pathname.split('models/')[1];
  const [ cookies ] = useCookies([ 'userAuth' ]);
  const [ availLoggedIn, setAvailLoggedIn ] = useState();
  const token = cookies.archvision_token;
  const creditBalance = userDataStore?.data?.user_points || 0;
  const selectedTheme = userDataStore.userPreferences.theme;
  const showDocumentation = userDataStore.userPreferences.showDocumentation;
  const firstRun = JSON.parse(localStorage.getItem('fovFirstRun')) ?? true;
  const userModels = userDataStore.userModels;
  const userId = userDataStore?.data?.userId;
  const userEmail = userDataStore?.data?.email;
  const userRoles = userDataStore?.data?.roles || [];
  const navigate = useNavigate();
  const searchFieldRef = useRef('');
  const params = paramsToObject(decode(paramsFromURL()));

  useEffect(() => {
    if (!showSearch) {
      return;
    }
    if (!params.search) {
      searchFieldRef.current.value = '';
    }
  }, [ params.search ]);

  const menus = Object.freeze({
      create: setCreatePopover,
      help: setHelpPopover,
      profile: setProfilePopover,
      credits: setCreditsPopover,
  });

  const hasHWA = (() => {
    const test = (force = false) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: force });
      ctx.moveTo(0, 0),
      ctx.lineTo(120, 121);
      ctx.stroke();
      return ctx.getImageData(0, 0, 200, 200).data.join();
    };
    return test(true) !== test(false);
  })();

  const [ showHWAModal, setShowHWAModal ] = useState(!hasHWA);

  const handleBlur = () => {
    const value = searchFieldRef.current.value;

    if (value === '') {
      navigate('/creators');
      dispatch(clearMessages());
      return;
    }
    navigate(`/creators?page=1&per_page=50&order_by=CREATED_NEW_TO_OLD&search=${value}`);
    dispatch(clearMessages());
  };

  const setOtherMenusClosed = openMenu => {
      Object.keys(menus).forEach(key => {
          if (typeof menus[key] === 'function' && key !== openMenu) menus[key](false);
      });

  };

  const fileName = useMemo(() => {
    return rpcStore.publishName || rpcStore.modelMetadata?.title;
  }, [ guid, rpcStore.publishName, rpcStore.modelMetadata ]);

  useEffect(() => {
      if (modelsPage && guid) dispatch(getModelMetadata(guid));
      return () => {
          dispatch(clearModelMetadata());
          dispatch(setPublishName(''));
      };

  }, [ modelsPage ]);

  const clearFirstRunExperience = () => {
    if (firstRun) {
      const message = 'Toggle references from the help menu, or by pressing F1.';
      const config = { type: MESSAGE_TYPES.info, timer: 6 };

      localStorage.removeItem('fovReadItems');
      localStorage.setItem('fovFirstRun', false);

      dispatch(addMessage([ { message, config } ]));
    }

    dispatch(setUserPreferences({ showDocumentation: !showDocumentation }));
  };

  const toggleDocumentation = e => {
    const key = e.key;

    if (key === 'F1') {
      e.preventDefault();
      clearFirstRunExperience();
    }
  };

  useEffect(() => {
    dispatch(getRpcCategories(token));
  }, []);

  useEffect(() => {
    removeEventListener('keydown', toggleDocumentation);
    window.addEventListener('keydown', toggleDocumentation);

    return () => removeEventListener('keydown', toggleDocumentation);
  }, [ showDocumentation ]);

  const submitImageBasedCreate = useCallback((values, debugState, onComplete) => {
    const wssArgs = {
      token,
      dispatch,
      userModels,
      handleCreditUpdate: () => dispatch(getUserData(token)),
    };

    const createArgs = {
      token,
      userId,
      userEmail,
      payload: values,
      debugState,
      onComplete,
      onSuccess: () => setTimeout(() => navigate('/jobs'), 500),
      handleSockets: () => wssCallback(wssArgs),
    };

    const message = 'Packaging model…';
    const config = {
      type: MESSAGE_TYPES.loading,
      showModal: true,
    };

    values.email = userDataStore?.data?.email;
    values.author = userDataStore?.data?.name;

    dispatch(addMessage([ { message, config } ]));
    setModalOpen(false);
    dispatch(createRpc(createArgs));
  }, [ userDataStore, dispatch, createRpc, addMessage, rpcCreateType ]);

  const openRpcModal = type => {
    if (!userId) {
      window.location.href = buildAuthUrl();
      return;
    }
    setRpcCreateType(type);
    setModalOpen(true);
    dispatch(getAvailChannels(cookies.archvision_token, setAvailLoggedIn));
    setCreatePopover(false);
  };

  const logout = () => {
    const customMessage = userId ? 'Logging out...' : 'Redirecting to login...';
    clearImpersonationBackup();
    dispatch(invalidateAuth(token, () => removeCookie(ARCHVISION_TOKEN), customMessage));
  };

  const register = () => {
    window.location.href = 'https://archvision.com/wp-login.php?action=register';
  };

  const loginAvail = () => {
    setAvailModalOpen(true);
  };

  const logoutAvail = () => {
    dispatch(availLogout(cookies.archvision_token, setAvailLoggedIn));
  };

  const closeOktaLoginModal = () => {
    setAvailModalOpen(false);
  };

  const loginAvailButton = () => {
    return (
      <Fragment key={420}>
        <ProtectedContent>
          <NormalMenuItem theme={theme} $selectedTheme={selectedTheme} onClick={loginAvail}>
            Log in to AVAIL
          </NormalMenuItem>

          <VerticalSpacer size={10} />
          <FovDivider />
          <VerticalSpacer size={10} />
        </ProtectedContent>
      </Fragment>
    );
  };

  const logoutAvailButton = () => {
    return (
      <Fragment key={419}>
        <ProtectedContent>
          <NormalMenuItem theme={theme} $selectedTheme={selectedTheme} onClick={logoutAvail}>
            Log out of AVAIL
          </NormalMenuItem>

          <VerticalSpacer size={10} />
          <FovDivider />
          <VerticalSpacer size={10} />
        </ProtectedContent>
      </Fragment>
    );
  };

  const UserMenu = () => {
    const menuItems = [
      {
        key: 'jobs',
        path: `/jobs${onJobs ? location.search : ''}`,
        text: 'My Jobs',
        allowedRoles: allRoles,
      },
      {
        key: 'my-files',
        path: `/my-files${onMyFiles ? location.search : ''}`,
        text: 'My Files',
        allowedRoles: allRoles,
      },
      {
        key: 'purchased-models',
        path: `/purchased-models`,
        text: 'Purchased',
        allowedRoles: allRoles,
      },
    ];

    // Add Team Models page if the user is part of a team
    if (userDataStore?.data?.teams &&
        Array.isArray(userDataStore.data.teams) &&
        userDataStore.data.teams.length > 0
    ) {
        menuItems.push({
            key: 'team-models',
            path: '/team-models',
            text: 'Team Models',
            allowedRoles: allRoles,
        });

        // Add Team Management page if the user has an admin role for a team
        if (userDataStore.data.teams.find(team => team.role === 'admin')) {
            menuItems.push({
                key: 'team-admin',
                path: '/team-admin/members',
                text: 'Team Management',
                allowedRoles: allRoles,
            });
        }
    }

    if (hasAccess(userRoles, superUsers)) {
      menuItems.push({
        key: 'archvision-admin',
        path: '/archvision-admin/creators',
        text: 'ArchVision Admin',
        allowedRoles: superUsers,
      });
    }


    return (
      <div>
        {userId && menuItems.map((item, index) => {
          const { path, text, allowedRoles } = item;
          return (
            <ProtectedContent key={index} requiredRoles={allowedRoles}>
              {(index !== 0) && (
                <VerticalSpacer size={10} />
              )}
              <MenuLink
                theme={theme}
                $selectedTheme={selectedTheme}
                to={path || ''}
              >
                <MenuItem
                  theme={theme}
                  $selectedTheme={selectedTheme}
                  active={createPopover}
                >
                  {text}
                </MenuItem>
              </MenuLink>
              {(index === menuItems.length - 1) && (
                <>
                  <VerticalSpacer size={10} />
                  <FovDivider />
                  <VerticalSpacer size={10} />
                </>
              )}
            </ProtectedContent>
          );
        })}
        <MenuItem
          key="logout"
          theme={theme}
          $selectedTheme={selectedTheme}
          onClick={logout}
        >
          {userId ? 'Log out' : 'Login'}
        </MenuItem>

        {!userId && (
          <div>
            <VerticalSpacer size={10} />
            <MenuItem
              key="register"
              theme={theme}
              $selectedTheme={selectedTheme}
              onClick={register}
            >
              Register
            </MenuItem>
          </div>
        )}
      </div>
    );
  };

  const helpMenu = () => {
    const menuItems = [
      {
        path: Object.values(docsItems)[0][0].path,
        text: 'Documentation',
      },
      {
        path: 'https://docs.google.com/forms/d/e/1FAIpQLSeXIlMYEQ8HXvqkiGBiZexcByy9QGEAlSEidY5EWnJZLE5OlQ/viewform',
        text: 'Contact support',
      },
      {
        callback: () => clearFirstRunExperience(),
        text: `${showDocumentation ? 'Hide' : 'Show'} references (F1)`,
        useDivider: true,
      },
      { path: '//archvision.com/terms-and-conditions/',
        text: 'Terms and Conditions',
      },
      {
        path: '//archvision.com/privacy-policy/',
        text: 'Privacy Policy',
      },
    ];

    return menuItems.map((item, index) => {
      const { path, text, callback, useDivider } = item;

      return (
        <div key={index}>
          {(index !== 0) && (
            <VerticalSpacer size={10} />
          )}

          <MenuLink
            key={index}
            theme={theme}
            $selectedTheme={selectedTheme}
            to={path || ''}
            rel='noreferrer'
            target={path?.includes('//') ? '_blank' : '_self'}
          >
            <MenuItem
              theme={theme}
              $selectedTheme={selectedTheme}
              onClick={e => {
                if (callback) {
                  e.preventDefault();
                  callback();
                }
              }}
            >
              {text}
              {index !== menuItems.length - 1 && <VerticalSpacer size={10} />}
              {useDivider && (
                <>
                  <FovDivider />
                  <VerticalSpacer size={10} />
                </>
              )}
            </MenuItem>
          </MenuLink>
        </div>
      );
    });
  };

  const handleExploreClick = () => {
    navigate('/creators');
    dispatch(clearMessages());
  };

  const handleMyfilesClick = () => {
    if (!userId) {
      window.location.href = buildAuthUrl();
      return;
    }
    navigate('/my-files');
    dispatch(clearMessages());
  };

  const handlePricingClick = () => {
    window.open(
      'https://archvision.com/product/credits/',
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleAddCreditsClick = () => {
    window.open(
      'https://archvision.com/product/credits/',
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleCreateClick = () => {
    if (!userId) {
      window.location.href = buildAuthUrl();
      return;
    }
  };

  const createMenu = () => {
    const menuItems = [
      {
        path: null,
        text: 'From File',
        callback: () => openRpcModal(),
        allowedRoles: allRoles,
      },
      {
        path: '/create',
        text: 'From gLTF',
        callback: () => {
          setCreatePopover(false);
          dispatch(setSerializedData(null));
          dispatch(clearUpdateData());
          dispatch(setReinitializeCanvas(true));
          dispatch(setExportModel(false));
          dispatch(setExportedModelData(null));
          dispatch(setGuidURL(null));
        },
        allowedRoles: allRoles,
      },
      {
        path: '/image-canvas',
        text: 'From Image',
        badge: 'BETA',
        callback: () => setCreatePopover(false),
        allowedRoles: allRoles,
      },
    ];

    return menuItems.map((item, index) => {
      const { path, text, badge, allowedRoles, callback } = item;

      const menuItemContent = (
        <MenuItem
          theme={theme}
          $selectedTheme={selectedTheme}
          active={createPopover}
        >
          {badge ? (
            <MenuItemContainer>
              <span>{text}</span>
              <BetaBadge>{badge}</BetaBadge>
            </MenuItemContainer>
          ) : (
            text
          )}
        </MenuItem>
      );

      return (
        <ProtectedContent key={index} requiredRoles={allowedRoles}>
          {(index !== 0) && (
            <VerticalSpacer size={10} />
          )}

          {(!path && (
            <BasicLink
              theme={theme}
              $selectedTheme={selectedTheme}
              onClick={callback}
            >
              {menuItemContent}
            </BasicLink>
          ))}

          {path && (
            <MenuLink
              theme={theme}
              $selectedTheme={selectedTheme}
              to={path}
              onClick={callback}
            >
              {menuItemContent}
            </MenuLink>
          )}
        </ProtectedContent>
      );
    });
  };

  return (
    <AppHeaderContainer $selectedTheme={selectedTheme}>
      <HeaderLogoGroup>
        <Link to='https://archvision.com/'>
          <ArchvisionLogo />
        </Link>
        <VerticalDivider theme={theme} $selectedTheme={selectedTheme} />
        <LogoContainer>
          <Link to={userId ? '/my-files' : '/'}>
            <Logo />
            <DocNote path={[ 'header', 'logo' ]} top={23} left={110} />
          </Link>
        </LogoContainer>
        <HeaderLogoGroup space='30px'>
          <ExploreButton
            theme={theme}
            $selectedTheme={selectedTheme}
            onClick={handleExploreClick}
          >
            Explore
          </ExploreButton>
          { !modelsPage && (
          <HeaderButton
            theme={theme}
            $selectedTheme={selectedTheme}
            onClick={handleMyfilesClick}
          >
            My&nbsp;files
          </HeaderButton>
          )}
          {userId ? (
            <FovPopover
              position="bottom"
              top={45}
              left={-21}
              content={createMenu()}
              callback={() => {
                setCreatePopover(!createPopover);
                setOtherMenusClosed('create');
              }}
              isVisible={createPopover}
            >
              <HeaderButton
                theme={theme}
                $selectedTheme={selectedTheme}
                active={createPopover}
              >
                Create
              </HeaderButton>
            </FovPopover>
          ) : (
            <HeaderButton
              theme={theme}
              $selectedTheme={selectedTheme}
              active={false}
              onClick={handleCreateClick}
            >
              Create
            </HeaderButton>
          )}
          {!userId && (
            <HeaderButton
              theme={theme}
              $selectedTheme={selectedTheme}
              onClick={handlePricingClick}
            >
              Pricing
            </HeaderButton>
          )}
        </HeaderLogoGroup>
      </HeaderLogoGroup>
      {(teamsPage || modelsPage) && (
            <FileNameContainer>
              <AppHeaderFileName
                path={(location.pathname?.includes('/models') && '/my-files')
                  || (location.pathname?.includes('/creator-models') && '/creators')
                  || (location.pathname?.includes('/team-models') && '..')
                }
                pageName={location.pathname?.includes('/team-models') ? 'Teams' : 'Models'}
                fileName={fileName}
              />
            </FileNameContainer>
      )}
      {showHWAModal && (
        <FovModal
          theme={theme}
          $selectedTheme={selectedTheme}
          visible={showHWAModal}
          title="Enable hardware acceleration for best performance"
          onClose={() => setShowHWAModal(false)}
          noOverflow={true}
        >
          <div>
            <p>
              We’ve detected that your browser’s hardware acceleration is turned <strong>off</strong>, which can
              slow down FOVEA’s 3D preview and image-heavy pages.
            </p>
          </div>
          <Instructions>
            <div>
              How to turn it on in Chrome:
              <ol>
                <li>Open your Chrome settings by clicking the button below or navigating to{' '}
                  <code>chrome://settings/system</code>.
                </li>
                <li>Find “Use hardware acceleration when available” and toggle it <strong>on</strong>.</li>
                <li>Restart Chrome to apply the change.</li>
              </ol>
            </div>
          </Instructions>

        </FovModal>
      )}
      {onWelcome && (
        <MenuContainer>
          <Search>
            <SearchInputField
              width={238}
              disabled={false}
              ref={searchFieldRef}
              defaultValue={''}
              onChange={e => searchFieldRef.current.value = e.target.value}
              onKeyUp={e => e.key === 'Enter' && e.target.blur()}
              onBlur={handleBlur}
            />
          </Search>
        </MenuContainer>
      )}
      <ProfileMenuContainer theme={theme} $selectedTheme={selectedTheme}>
        <div>
          <ThemeSwitcher />
          <HorizontalSpacer size={35} />
        </div>

        <FovPopover
          position='bottom'
          top={45}
          left={-101}
          $minWidth={232}
          content={helpMenu()}
          isVisible={helpPopover}
          callback={() => {
              setHelpPopover(!helpPopover);
              setOtherMenusClosed('help');
          }}
        >
          <DocNote path={[ 'header', 'helpMenu' ]} bottom={-25} />
          <InfoAndSupport
            theme={theme}
            $selectedTheme={selectedTheme}
          >
              <HelpIcon>?</HelpIcon>
          </InfoAndSupport>

          <HorizontalSpacer size={10} />
        </FovPopover>

        <Profile>
          <FovPopover
            position='bottom'
            top={45}
            content={[
              typeof CefSharp === 'undefined' ? (availLoggedIn ? logoutAvailButton() : loginAvailButton()) : null,
              UserMenu(),
            ]}
            isVisible={profilePopover}
            callback={() => {
              setProfilePopover(!profilePopover);
              setOtherMenusClosed('profile');
            }}
          >
            <DocNote path={[ 'header', 'profileMenu' ]} bottom={-31} />

            <UserOutlined /> <HorizontalSpacer size={15} />
            {
              (userDataStore.data?.firstName && userDataStore.data.lastName)
              ? <>{(userDataStore.data?.firstName)}<HorizontalSpacer size={7} />{(userDataStore.data?.lastName)}</>
              : <>
                {(userDataStore.data?.email)
                  ? (userDataStore.data?.email)
                  : <>Sign<HorizontalSpacer size={7} />In</>
                }
                </>
            }
          </FovPopover>
          {userId && (
            <CreditsContainer>
              <FovPopover
                position='bottom'
                top={30}
                right={-30}
                content={(
                  <AddCreditsButton
                    type='button'
                    theme={theme}
                    $selectedTheme={selectedTheme}
                    onClick={handleAddCreditsClick}
                  >
                    Add more credits...
                  </AddCreditsButton>
                )}
                isVisible={creditsPopover}
                callback={() => {
                  setCreditsPopover(!creditsPopover);
                  setOtherMenusClosed('credits');
                }}
              >
                <Credits type='button'>
                  <CreditsOutlined width={18} height={18} />
                  <HorizontalSpacer size={10} />

                  <strong>
                    {creditBalance?.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                  </strong>
                </Credits>
              </FovPopover>
            </CreditsContainer>
          )}
        </Profile>
      </ProfileMenuContainer>

      <RPCForm
        isVisible={modalOpen}
        setIsVisible={setModalOpen}
        onSubmit={(values, debugState, onComplete) => submitImageBasedCreate(values, debugState, onComplete)}
        isModelForSaving={true}
        setAvailModalOpen={setAvailModalOpen}
        setAvailLoggedIn={setAvailLoggedIn}
      />

      <AvailLoginContainer
        isVisible={availModalOpen}
        closeMe={closeOktaLoginModal}
        onCancel={() => setAvailModalOpen(false)}
        setAvailLoggedIn={tf => {
          setAvailLoggedIn(tf);
        }}
      />
    </AppHeaderContainer>
  );
});

export { AppHeader };
